// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewWorkspace.test.tsx
import {
  type AskPunttComparisonRegion,
  type AskPunttVersionComparison,
} from "@mg/schemas/src/christo/versionComparison";
import { useTranslation } from "react-i18next";

import { describeRegion } from "./versionChanges";

/**
 * The strip over a version comparison: how many windows changed, where the
 * picked change or finding sits, and the one legend for the comparison: each
 * pin shape beside the sign its diff rows carry. Stepping is J and K, or a
 * click on a change.
 */
export function AskV2ReviewCompareStatus({
  comparison,
  changeAt,
  steppedRegion,
  hasSelectedComment,
  selectedRegion,
}: {
  comparison: AskPunttVersionComparison | null;
  changeAt: number | null;
  steppedRegion: AskPunttComparisonRegion | null;
  hasSelectedComment: boolean;
  selectedRegion: AskPunttComparisonRegion | null;
}) {
  const { t } = useTranslation("ui");
  return (
    <div
      className="flex min-h-8 items-center gap-2 border-b border-puntt-neutral-gray-5 bg-base-white px-4 py-1 text-[12px] text-puntt-neutral-gray-11"
      data-testid="ask-v2-review-compare-strip"
    >
      {/* why: a status region is read out whole on every change, so the
          static legend sits beside it, not inside it. */}
      <div
        className="flex min-w-0 items-center gap-2"
        data-testid="ask-v2-review-compare-status"
        role="status"
        aria-label={t("askV2.review.compareStatusLabel")}
      >
        {comparison ? (
          <>
            <span
              className="shrink-0 font-medium text-puntt-neutral-gray-12"
              data-testid="ask-v2-review-compare-change-count"
            >
              {t("askV2.review.compareChangeCount", {
                count: comparison.regions.length,
              })}
            </span>
            {steppedRegion && changeAt != null ? (
              <span
                className="min-w-0 truncate"
                data-testid="ask-v2-review-compare-change-current"
              >
                <span className="font-medium text-puntt-neutral-gray-12">
                  {t("askV2.review.compareChangePosition", {
                    current: changeAt + 1,
                    total: comparison.regions.length,
                  })}
                </span>
                {describeRegion(steppedRegion)
                  ? `: ${describeRegion(steppedRegion)}`
                  : null}
              </span>
            ) : hasSelectedComment ? (
              <span
                className="min-w-0 truncate"
                data-testid="ask-v2-review-compare-finding-status"
              >
                {selectedRegion
                  ? t("askV2.review.compareFindingChanged", {
                      number: selectedRegion.number,
                    })
                  : t("askV2.review.compareFindingUnchanged")}
              </span>
            ) : null}
          </>
        ) : (
          <span data-testid="ask-v2-review-compare-not-compared">
            {t("askV2.review.compareNotCompared")}
          </span>
        )}
      </div>
      {comparison ? (
        <span
          className="ml-auto flex shrink-0 items-center gap-3"
          data-testid="ask-v2-review-compare-legend"
        >
          <span
            className="flex items-center gap-1"
            data-testid="ask-v2-review-compare-legend-before"
          >
            <span
              aria-hidden="true"
              className="size-3 rounded-full border-2 border-puntt-gold-10 bg-base-white"
            />
            {t("askV2.review.compareLegendRing")}
          </span>
          <span
            className="flex items-center gap-1"
            data-testid="ask-v2-review-compare-legend-after"
          >
            <span
              aria-hidden="true"
              className="size-3 rounded-full bg-puntt-blue-12"
            />
            {t("askV2.review.compareLegendSolid")}
          </span>
        </span>
      ) : null}
    </div>
  );
}
